import React from "react";
import "./BackgroundControlColorMenu.css";
import { colorPickerIcon } from "../../../../icons/assets";
import { generateColorShades } from "../../../../utils/colorShadeGenerator";
import CustomColorPicker from "../../../../components/shared/CustomColorPicker";

const BackgroundControlColorMenu = ({
  onColorSelect = () => {},
  onApplyBackground = () => {},
  currentBgColor = "transparent",
}) => {
  const [customColor, setCustomColor] = React.useState("#ffffff");
  const [selectedColor, setSelectedColor] = React.useState(null);
  const [showPicker, setShowPicker] = React.useState(false);
  const [recentlyUsed, setRecentlyUsed] = React.useState([]);

  const [selectedBaseColor, setSelectedBaseColor] = React.useState(null);
  const [isGeneratingShades, setIsGeneratingShades] = React.useState(false);
  const [generatedShades, setGeneratedShades] = React.useState([]);
  const [selectedShade, setSelectedShade] = React.useState(null);

  const shadeGenerationId = React.useRef(0);

  // 23 colors + color picker = 24 swatches in the palette grid
  const palette = [
    "#000000",
    "#FFFFFF",
    "#2D3436",
    "#636E72",
    "#B2BEC3",
    "#DFE6E9",
    "#FF7675",
    "#FF5B5B",
    "#D63031",
    "#E17055",
    "#F79F1F",
    "#FDCB6E",
    "#FFEAA7",
    "#FEEAC9",
    "#C4E538",
    "#00B894",
    "#0ABAB5",
    "#81ECEC",
    "#0984E3",
    "#74B9FF",
    "#6C5CE7",
    "#A29BFE",
    "#FD79A8",
  ];

  const addToRecentlyUsed = (color) => {
    setRecentlyUsed((prev) => {
      const filtered = prev.filter((c) => c !== color);
      return [color, ...filtered].slice(0, 5);
    });
  };

  const normalizeColor = (color) => {
    if (!color || color === "transparent") return null;

    const colorMap = {
      white: "#ffffff",
      black: "#000000",
      red: "#ff0000",
      green: "#00ff00",
      blue: "#0000ff",
      yellow: "#ffff00",
      cyan: "#00ffff",
      magenta: "#ff00ff",
    };

    const lowerColor = color.toLowerCase();
    if (colorMap[lowerColor]) {
      return colorMap[lowerColor];
    }

    return color.toLowerCase();
  };

  const normalizeShadeBaseColor = (color) => {
    const normalized = normalizeColor(color);
    if (!normalized) return null;

    const hex8Match = /^#([0-9a-f]{8})$/i.exec(normalized);
    if (hex8Match) {
      return `#${hex8Match[1].slice(0, 6)}`;
    }

    return normalized;
  };

  React.useEffect(() => {
    const normalized = normalizeColor(currentBgColor);

    if (!normalized) {
      setSelectedColor((prev) => (prev == null ? prev : null));
      return;
    }

    const matchingPaletteColor = palette.find((c) => {
      const normalizedPalette = normalizeColor(c);
      return normalizedPalette === normalized;
    });

    if (matchingPaletteColor) {
      setSelectedColor((prev) =>
        prev === matchingPaletteColor ? prev : matchingPaletteColor
      );
      setCustomColor((prev) => (prev === normalized ? prev : normalized));
    } else {
      setSelectedColor((prev) => (prev === normalized ? prev : normalized));
      setCustomColor((prev) => (prev === normalized ? prev : normalized));
    }
  }, [currentBgColor]);

  const handleBaseColorSelect = (color) => {
    shadeGenerationId.current += 1;
    const currentGenerationId = shadeGenerationId.current;

    setSelectedBaseColor(color);
    setGeneratedShades([]);
    setSelectedShade(null);
    setIsGeneratingShades(true);

    const runGeneration = () => {
      const shades = generateColorShades(color, 30);
      if (shadeGenerationId.current !== currentGenerationId) return;
      setGeneratedShades(shades);
      setIsGeneratingShades(false);
    };

    if (typeof window !== "undefined" && window.requestIdleCallback) {
      window.requestIdleCallback(runGeneration, { timeout: 300 });
    } else {
      setTimeout(runGeneration, 0);
    }
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setCustomColor(color);
    addToRecentlyUsed(color);
    onColorSelect(color);
    onApplyBackground(color);

    const baseColor = normalizeShadeBaseColor(color);
    if (baseColor) {
      handleBaseColorSelect(baseColor);
    }
  };

  const handleShadeClick = (shade) => {
    setSelectedShade(shade);
    addToRecentlyUsed(shade);
    onApplyBackground(shade);
  };

  const handleCustomApply = (picked) => {
    setSelectedColor(picked);
    setCustomColor(picked);
    addToRecentlyUsed(picked);
    onApplyBackground(picked);
    const baseColor = normalizeShadeBaseColor(picked);
    if (baseColor) {
      handleBaseColorSelect(baseColor);
    }
  };

  const isCustomSelected =
    Boolean(selectedColor) &&
    !palette.some((c) => normalizeColor(c) === normalizeColor(selectedColor));

  return (
    <div className="background-control-color-container">
      <CustomColorPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        color={customColor}
        onApply={handleCustomApply}
        ignoreCloseSelectors={["#color-picker"]}
      />

      {recentlyUsed.length > 0 && (
        <>
          <p>Recently Used</p>
          <div className="custom-color-layout">
            {recentlyUsed.map((color, index) => (
              <div
                key={index}
                className={`color-option ${selectedColor === color ? "selected" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => handleColorSelect(color)}
                role="button"
                tabIndex={0}
              />
            ))}
          </div>
        </>
      )}

      <p>Palette</p>

      <div className="palette-grid">
        <img
          className={`color-option palette-color-picker ${
            isCustomSelected ? "selected" : ""
          }`}
          id="color-picker"
          src={colorPickerIcon}
          alt="Custom color"
          role="button"
          tabIndex={0}
          onClick={() => setShowPicker(!showPicker)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setShowPicker((prev) => !prev);
            }
          }}
          title="Custom color"
        />
        {palette.map((c, i) => (
          <div
            key={i}
            role="button"
            tabIndex={0}
            className={`color-option ${selectedColor === c ? "selected" : ""}`}
            style={{ backgroundColor: c }}
            onClick={() => handleColorSelect(c)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleColorSelect(c);
              }
            }}
          />
        ))}
      </div>

      {selectedBaseColor && (
        <div className="shades-section">
          <p className="shades-heading">Shades</p>

          {isGeneratingShades ? (
            <div className="shades-preloader-container">
              <div className="shades-preloader" />
              <p className="shades-loading-text">Generating shades...</p>
            </div>
          ) : generatedShades.length > 0 ? (
            <div className="shades-grid">
              {generatedShades.map((shade, index) => (
                <div
                  key={index}
                  className={`shade-swatch ${selectedShade === shade ? "shade-selected" : ""}`}
                  style={{ backgroundColor: shade }}
                  onClick={() => handleShadeClick(shade)}
                  role="button"
                  tabIndex={0}
                  title={shade}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleShadeClick(shade);
                    }
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>
      )}

      <button
        className="apply-background-btn"
        onClick={() => {
          const colorToApply = selectedColor || customColor || "#ffffff";
          onApplyBackground(colorToApply);
        }}
        disabled={!selectedColor && !customColor}
      >
        Apply
      </button>
    </div>
  );
};

export default BackgroundControlColorMenu;
